import { ACTIVE_EXPERIMENTS } from './experiments';
import { safeGetStorageItem, safeRemoveStorageItem, safeSetStorageItem } from './browserStorage';
import { isPreviewExperimentOverrideHost } from './experimentOverrideHosts';

export const EXPERIMENT_PREVIEW_STORAGE_KEY = 'botspot:experiment-preview-overrides:v1';
export const EXPERIMENT_PREVIEW_OVERRIDE_EVENT = 'botspot:experiment-preview-overrides-change';

const PREVIEW_EXPERIMENTS = [
  {
    registryKey: 'MARKETPLACE_DECISION_DESK_2026_08',
    label: 'Marketplace decision desk',
    route: '/marketplace',
  },
  {
    registryKey: 'PRICING_PAGE_REDESIGN_2026_07',
    label: 'Pricing page',
    route: '/pricing',
  },
] as const;

export const EXPERIMENT_PREVIEW_CATALOG = PREVIEW_EXPERIMENTS.map((entry) => {
  const experiment = ACTIVE_EXPERIMENTS[entry.registryKey];

  return {
    ...entry,
    description: experiment.description,
    experimentName: experiment.name,
    featureFlag: experiment.featureFlag,
    posthogExperimentId:
      'posthog_experiment_id' in experiment ? experiment.posthog_experiment_id : undefined,
    status: experiment.status,
    variants: Object.entries(experiment.variants).map(([value, label]) => ({ label, value })),
  };
});

type PreviewOverrides = Record<string, string>;

type PreviewOverrideEventDetail = {
  featureFlagKey?: string;
  variant?: string | null;
};

const getCurrentHostname = (): string => globalThis.window?.location?.hostname || '';

const parseStoredOverrides = (rawOverrides: string): PreviewOverrides => {
  const parsedOverrides = JSON.parse(rawOverrides);
  if (
    parsedOverrides === null ||
    Array.isArray(parsedOverrides) ||
    Object.prototype.toString.call(parsedOverrides) !== '[object Object]'
  ) {
    return {};
  }

  const candidateEntries = Object.entries(parsedOverrides);
  return EXPERIMENT_PREVIEW_CATALOG.reduce<PreviewOverrides>((overrides, experiment) => {
    const candidateVariant = candidateEntries.find(
      ([featureFlag]) => featureFlag === experiment.featureFlag,
    )?.[1];
    const storedVariant = experiment.variants.find(({ value }) => value === candidateVariant);
    if (storedVariant) overrides[experiment.featureFlag] = storedVariant.value;
    return overrides;
  }, {});
};

const readStoredOverrides = (): PreviewOverrides => {
  const rawOverrides = safeGetStorageItem('sessionStorage', EXPERIMENT_PREVIEW_STORAGE_KEY);
  if (!rawOverrides) return {};

  try {
    return parseStoredOverrides(rawOverrides);
  } catch {
    return {};
  }
};

const dispatchOverrideChange = (detail: PreviewOverrideEventDetail): void => {
  const browserWindow = globalThis.window;
  if (!browserWindow) return;
  browserWindow.dispatchEvent(
    new browserWindow.CustomEvent(EXPERIMENT_PREVIEW_OVERRIDE_EVENT, { detail }),
  );
};

const getPreviewExperiment = (featureFlagKey: string) =>
  EXPERIMENT_PREVIEW_CATALOG.find((experiment) => experiment.featureFlag === featureFlagKey);

export const isExperimentPreviewAvailable = (hostname = getCurrentHostname()): boolean =>
  isPreviewExperimentOverrideHost(hostname);

export const getExperimentPreviewOverride = (
  featureFlagKey: string,
  validVariants: readonly string[] = [],
  hostname = getCurrentHostname(),
): string | null => {
  if (
    !isExperimentPreviewAvailable(hostname) ||
    validVariants.length === 0 ||
    !getPreviewExperiment(featureFlagKey)
  ) {
    return null;
  }

  const storedOverrides = readStoredOverrides();
  const storedVariant = Object.prototype.hasOwnProperty.call(storedOverrides, featureFlagKey)
    ? storedOverrides[featureFlagKey]
    : null;

  return storedVariant && validVariants.includes(storedVariant) ? storedVariant : null;
};

export const setExperimentPreviewOverride = (
  featureFlagKey: string,
  variant: string | null,
  validVariants: readonly string[],
  hostname = getCurrentHostname(),
): boolean => {
  if (!isExperimentPreviewAvailable(hostname)) return false;
  const previewExperiment = getPreviewExperiment(featureFlagKey);
  if (!previewExperiment) return false;
  if (
    variant !== null &&
    (!validVariants.includes(variant) ||
      !previewExperiment.variants.some(({ value }) => value === variant))
  ) {
    return false;
  }

  const storedOverrides = readStoredOverrides();
  if (variant === null) {
    delete storedOverrides[featureFlagKey];
  } else {
    storedOverrides[featureFlagKey] = variant;
  }

  let didPersist = true;
  if (Object.keys(storedOverrides).length === 0) {
    safeRemoveStorageItem('sessionStorage', EXPERIMENT_PREVIEW_STORAGE_KEY);
  } else {
    didPersist = safeSetStorageItem(
      'sessionStorage',
      EXPERIMENT_PREVIEW_STORAGE_KEY,
      JSON.stringify(storedOverrides),
    );
  }

  if (!didPersist) return false;

  dispatchOverrideChange({ featureFlagKey, variant });
  return true;
};

export const clearExperimentPreviewOverrides = (hostname = getCurrentHostname()): boolean => {
  if (!isExperimentPreviewAvailable(hostname)) return false;

  safeRemoveStorageItem('sessionStorage', EXPERIMENT_PREVIEW_STORAGE_KEY);
  dispatchOverrideChange({});
  return true;
};
